import { useEffect, useMemo, useState } from 'react';
import {
  acknowledgeJobDescription, approveJobDescription, createJobDescription,
  listEmployees, listJobDescriptions,
  type EmployeeRecord, type JobDescription,
} from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { Icon } from '../../components/Icon';
import { DateValue, Pending, asList } from './shared';

const LIST_FIELDS = [
  ['responsibilities', 'Key responsibilities'],
  ['dailyDuties', 'Daily duties'],
  ['weeklyDuties', 'Weekly duties'],
  ['monthlyDuties', 'Monthly duties'],
  ['kpis', 'KPIs'],
  ['authority', 'Authority'],
  ['restrictions', 'Restrictions'],
  ['skills', 'Skills'],
  ['qualifications', 'Qualifications'],
] as const;

type ListField = (typeof LIST_FIELDS)[number][0];

const splitLines = (v: string) => v.split('\n').map((s) => s.trim()).filter(Boolean);

export function JobDescriptions() {
  const { can, session } = useAuth();
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [versions, setVersions] = useState<JobDescription[]>([]);
  const [viewing, setViewing] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [form, setForm] = useState<Record<string, string>>({
    effectiveDate: '', changeReason: '', positionPurpose: '',
    confidentiality: '', informationSecurity: '', communicationRequirements: '', reviewFrequency: '',
    ...Object.fromEntries(LIST_FIELDS.map(([k]) => [k, ''])),
  });

  useEffect(() => {
    listEmployees({})
      .then((r) => {
        setEmployees(r.items);
        if (r.items.length && !selected) setSelected(r.items[0].id);
      })
      .catch((e: Error) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = (employeeId: string) => {
    if (!employeeId) return;
    listJobDescriptions(employeeId)
      .then((r) => { setVersions(r.items); setViewing(r.items[0]?.id ?? null); setError(null); })
      .catch((e: Error) => { setVersions([]); setError(e.message); });
  };

  useEffect(() => { load(selected); }, [selected]);

  const current = useMemo(() => versions.find((v) => v.id === viewing) ?? null, [versions, viewing]);
  const employee = useMemo(() => employees.find((e) => e.id === selected) ?? null, [employees, selected]);
  const isOwnRecord = session?.user.employeeId === selected;

  async function submitDraft(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const lists = Object.fromEntries(
        LIST_FIELDS.map(([k]) => [k, splitLines(form[k] ?? '')]).filter(([, v]) => (v as string[]).length),
      );
      const res = await createJobDescription({
        employeeId: selected,
        effectiveDate: form.effectiveDate,
        changeReason: form.changeReason,
        positionPurpose: form.positionPurpose || null,
        confidentiality: form.confidentiality || null,
        informationSecurity: form.informationSecurity || null,
        communicationRequirements: form.communicationRequirements || null,
        reviewFrequency: form.reviewFrequency || null,
        ...lists,
      });
      setNotice(`Draft ${res.version} created${res.supersedes ? ` — it will supersede ${res.supersedes} once approved` : ''}.`);
      setBuilding(false);
      load(selected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the draft.');
    } finally { setBusy(false); }
  }

  async function act(fn: () => Promise<unknown>, done: string) {
    setBusy(true); setError(null); setNotice(null);
    try { await fn(); setNotice(done); load(selected); }
    catch (err) { setError(err instanceof Error ? err.message : 'That action was refused.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="fileCheck" size="xs" /><span>HR Governance</span></span>
          <h1>Job Descriptions</h1>
        </div>
        <div className="toolbar">
          <select aria-label="Choose an employee" value={selected} onChange={(e) => { setSelected(e.target.value); setBuilding(false); }}>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.employee_code}</option>
            ))}
          </select>
          {can('jd.manage') && (
            <button className="btn-primary" type="button" onClick={() => setBuilding((v) => !v)}>
              <Icon name="fileSignature" size="xs" />
              <span>{building ? 'Close builder' : versions.length ? 'New version' : 'Draft JD'}</span>
            </button>
          )}
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}
      {notice && <div className="banner info" style={{ marginBottom: 14 }}>{notice}</div>}

      {building && (
        <form className="panel" onSubmit={submitDraft} style={{ marginBottom: 16 }}>
          <div className="panel-head">
            <strong>Job Description Builder — {employee ? `${employee.first_name} ${employee.last_name}` : ''}</strong>
            <span className="muted" style={{ fontSize: 12 }}>Blank fields stay Pending Confirmation. Nothing is invented.</span>
          </div>
          <div className="org-form">
            <label>
              <span>Effective date</span>
              <input type="date" required value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
            </label>
            <label>
              <span>Reason for this version</span>
              <input required minLength={3} placeholder="Initial appointment, responsibility update, promotion…"
                value={form.changeReason} onChange={(e) => setForm({ ...form, changeReason: e.target.value })} />
            </label>
            <label className="org-form-wide">
              <span>Position purpose</span>
              <textarea rows={2} placeholder="Why this position exists"
                value={form.positionPurpose} onChange={(e) => setForm({ ...form, positionPurpose: e.target.value })} />
            </label>
            {LIST_FIELDS.map(([key, label]) => (
              <label key={key} className="org-form-wide">
                <span>{label} <em className="muted">one per line</em></span>
                <textarea rows={3} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
              </label>
            ))}
            <label><span>Confidentiality</span><input value={form.confidentiality} onChange={(e) => setForm({ ...form, confidentiality: e.target.value })} /></label>
            <label><span>Information security</span><input value={form.informationSecurity} onChange={(e) => setForm({ ...form, informationSecurity: e.target.value })} /></label>
            <label><span>Communication requirements</span><input value={form.communicationRequirements} onChange={(e) => setForm({ ...form, communicationRequirements: e.target.value })} /></label>
            <label><span>Review frequency</span><input placeholder="Annual, half-yearly…" value={form.reviewFrequency} onChange={(e) => setForm({ ...form, reviewFrequency: e.target.value })} /></label>
          </div>
          <div className="org-form-actions">
            <button className="btn-primary" type="submit" disabled={busy}>Create draft</button>
            <span className="muted">A draft changes nothing until someone approves it, and nobody may approve their own.</span>
          </div>
        </form>
      )}

      <div className="org-grid">
        <div className="panel">
          <div className="panel-head">
            <strong>{current ? `${current.version} — ${current.status}` : 'No job description'}</strong>
            {current && (
              <span className="toolbar">
                {current.status === 'draft' && can('jd.approve') && (
                  <button type="button" disabled={busy} onClick={() => act(() => approveJobDescription(current.id), `${current.version} approved and published.`)}>Approve</button>
                )}
                {current.status === 'active' && !current.acknowledged && isOwnRecord && can('jd.acknowledge') && (
                  <button type="button" disabled={busy} onClick={() => act(() => acknowledgeJobDescription(current.id), 'Acknowledged.')}>Acknowledge</button>
                )}
              </span>
            )}
          </div>

          {!current ? (
            <div className="empty">
              <strong>No job description on file</strong>
              <p>This person's responsibilities, authority and limits are undocumented.</p>
            </div>
          ) : (
            <div className="org-jd">
              <dl className="org-jd-meta">
                <div><dt>Designation</dt><dd><Pending>{current.designation}</Pending></dd></div>
                <div><dt>Effective</dt><dd><DateValue value={current.effectiveDate} /></dd></div>
                <div><dt>Version</dt><dd className="mono">{current.version}</dd></div>
                <div><dt>Change reason</dt><dd><Pending>{current.changeReason}</Pending></dd></div>
                <div><dt>Approved</dt><dd><DateValue value={current.approvedAt} /></dd></div>
                <div><dt>Acknowledged</dt><dd>{current.acknowledged ? <DateValue value={current.acknowledgedAt} /> : <Pending />}</dd></div>
              </dl>

              <h3>Position purpose</h3>
              <p><Pending>{current.positionPurpose}</Pending></p>

              {(['responsibilities', 'kpis', 'authority', 'restrictions'] as ListField[]).map((key) => {
                const items = asList(current[key as keyof JobDescription] as string[] | string);
                return (
                  <div key={key}>
                    <h3>{LIST_FIELDS.find(([k]) => k === key)![1]}</h3>
                    {items ? <ul className="org-bullets">{items.map((it) => <li key={it}>{it}</li>)}</ul> : <p><Pending /></p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="org-side">
          <div className="panel">
            <div className="panel-head"><strong>Version history</strong></div>
            {versions.length === 0 ? (
              <div className="empty"><strong>No versions</strong></div>
            ) : (
              <ul className="org-plain-list">
                {versions.map((v) => (
                  <li key={v.id}>
                    <button type="button" className={`org-version${v.id === viewing ? ' org-version-active' : ''}`} onClick={() => setViewing(v.id)}>
                      <span className="mono">{v.version}</span>
                      <span className={`org-chip org-chip-${v.status}`}>{v.status}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted org-footnote">
              A published version is never rewritten. A revision supersedes it and records what changed and why.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
